import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import AppShell from '@/components/layout/AppShell';
import TopLevelErrorBoundary from '@/components/layout/TopLevelErrorBoundary';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Login from '@/pages/auth/Login';
import Signup from '@/pages/auth/Signup';
import ResetPassword from '@/pages/auth/ResetPassword';
import LanguageTest from '@/pages/public/LanguageTest';
import StudentDashboard from '@/pages/student/StudentDashboard';
import CourseCatalog from '@/pages/student/CourseCatalog';
import StudentMyCourses from '@/pages/student/StudentMyCourses';
import CourseRoom from '@/components/course/room/CourseRoom';
import TakeExam from '@/pages/student/TakeExam';
import ExamReview from '@/pages/student/ExamReview';
import MyProgress from '@/pages/student/MyProgress';
import AcademicRecord from '@/pages/student/AcademicRecord';
import Finances from '@/pages/student/Finances';
import BrowseServices from '@/pages/student/BrowseServices';
import ServiceProductDetail from '@/pages/student/ServiceProductDetail';
import ServiceCheckout from '@/pages/student/ServiceCheckout';
import MyServiceOrders from '@/pages/student/MyServiceOrders';
import ServiceOrderDetail from '@/pages/student/ServiceOrderDetail';
import ServicePaymentResult from '@/pages/student/ServicePaymentResult';
import ServiceReceiptInvoice from '@/pages/student/ServiceReceiptInvoice';
import ServiceTracking from '@/pages/student/ServiceTracking';
import ServiceShippingUpgrade from '@/pages/student/ServiceShippingUpgrade';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ManageCourses from '@/pages/admin/ManageCourses';
import CourseBuilder from '@/pages/admin/CourseBuilder';
import ManageExams from '@/pages/admin/ManageExams';
import ManageAssignments from '@/pages/admin/ManageAssignments';
import ManageLiveSessions from '@/pages/admin/ManageLiveSessions';
import ManageAnnouncements from '@/pages/admin/ManageAnnouncements';
import ManageStudents from '@/pages/admin/ManageStudents';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminMarketing from '@/pages/admin/AdminMarketing';
import AdminAutomations from '@/pages/admin/AdminAutomations';
import AdminReports from '@/pages/admin/AdminReports';
import AccountingInvoices from '@/pages/admin/accounting/AccountingInvoices';
import AccountingInvoiceDetail from '@/pages/admin/accounting/AccountingInvoiceDetail';
import AccountingCustomers from '@/pages/admin/accounting/AccountingCustomers';
import AccountingItems from '@/pages/admin/accounting/AccountingItems';
import AccountingBills from '@/pages/admin/accounting/AccountingBills';
import AccountingTransactions from '@/pages/admin/accounting/AccountingTransactions';
import AdminCommissions from '@/pages/admin/AdminCommissions';
import AdminSubmissions from '@/pages/admin/AdminSubmissions';
import CreateInstructor from '@/pages/admin/CreateInstructor';
import AdminAccounts from '@/pages/admin/AdminAccounts';
import AdminProfile from '@/pages/admin/AdminProfile';
import InstructorDashboard from '@/pages/instructor/InstructorDashboard';
import InstructorCourses from '@/pages/instructor/InstructorCourses';
import InstructorAnnouncements from '@/pages/instructor/InstructorAnnouncements';
import AgencyDashboard from '@/pages/agency/AgencyDashboard';
import AgencyReferrals from '@/pages/agency/AgencyReferrals';
import AgencyEventLinks from '@/pages/agency/AgencyEventLinks';
import AgencyStudents from '@/pages/agency/AgencyStudents';
import AgencyCommissions from '@/pages/agency/AgencyCommissions';
import AgencyDocuments from '@/pages/agency/AgencyDocuments';
import AgencyProfile from '@/pages/agency/AgencyProfile';
import AgencySupport from '@/pages/agency/AgencySupport';
import StudentProfile from '@/pages/student/StudentProfile';
import InstructorProfile from '@/pages/instructor/InstructorProfile';
import LiveMeetingRoom from '@/pages/shared/LiveMeetingRoom';
import AdminStudentDetail from '@/pages/admin/AdminStudentDetail';
import AdminApplications from '@/pages/admin/AdminApplications';
import AdminApplicationDetail from '@/pages/admin/AdminApplicationDetail';
import AdminAgencies from '@/pages/admin/AdminAgencies';
import AdminChat from '@/pages/admin/AdminChat';
import Inbox from '@/pages/shared/Inbox';
import { InboxProvider } from '@/contexts/InboxContext';
import AdminWebsiteSignups from '@/pages/admin/AdminWebsiteSignups';
import AdminWebsiteSignupDetail from '@/pages/admin/AdminWebsiteSignupDetail';
import AdminWebsiteMessages from '@/pages/admin/AdminWebsiteMessages';
import WebsiteEngagementOverview from '@/pages/admin/engagement/WebsiteEngagementOverview';
import AdminContactInquiries from '@/pages/admin/engagement/AdminContactInquiries';
import AdminContactInquiryDetail from '@/pages/admin/engagement/AdminContactInquiryDetail';
import AdminNewsletterSubscribers from '@/pages/admin/engagement/AdminNewsletterSubscribers';
import AdminEvents from '@/pages/admin/AdminEvents';
import AdminLanguageTests from '@/pages/admin/AdminLanguageTests';
import AdminEventEditor from '@/pages/admin/AdminEventEditor';
import AdminEventRegistrations from '@/pages/admin/AdminEventRegistrations';
import AdminLegalPolicies from '@/pages/admin/AdminLegalPolicies';
import AdminEmailTemplates from '@/pages/admin/AdminEmailTemplates';
import AdminAgencyDetail from '@/pages/admin/AdminAgencyDetail';
import AdminGrades from '@/pages/admin/AdminGrades';
import ManagePrograms from '@/pages/admin/ManagePrograms';
import AdminProgramDetail from '@/pages/admin/AdminProgramDetail';
import AdminCourseIntakes from '@/pages/admin/AdminCourseIntakes';
import AdminServiceOrders from '@/pages/admin/AdminServiceOrders';
import AdminServiceOrderDetail from '@/pages/admin/AdminServiceOrderDetail';
import AdminServiceProducts from '@/pages/admin/AdminServiceProducts';
import AdminServiceProductEditor from '@/pages/admin/AdminServiceProductEditor';
import AdminServicePayments from '@/pages/admin/AdminServicePayments';
import AdminServiceFulfillment from '@/pages/admin/AdminServiceFulfillment';
import AdminServiceReconciliation from '@/pages/admin/AdminServiceReconciliation';
import { getMe } from '@/lib/api';
import { ADMIN_SCOPES, roleHome } from '@/lib/auth';

// App context — exposes the authenticated user as currentStudent / currentInstructor
// (linked via JWT through /api/me). Admins have neither set.
export const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

function AppContextProvider({ children }) {
  const { user, profile, role, signOut } = useAuth();
  const [currentStudent, setCurrentStudent] = useState(null);
  const [currentInstructor, setCurrentInstructor] = useState(null);
  const [currentAgency, setCurrentAgency] = useState(null);
  const [hydrating, setHydrating] = useState(false);

  // Pull the linked student/instructor record for the signed-in user via
  // /api/me. The backend derives identity from the JWT — never trust a
  // client-supplied id. Admins get {student: null, instructor: null}.
  const hydrate = useCallback(async () => {
    if (!user || !profile) {
      setCurrentStudent(null);
      setCurrentInstructor(null);
      setCurrentAgency(null);
      return;
    }
    setHydrating(true);
    try {
      const { data } = await getMe();
      setCurrentStudent(data?.student || null);
      setCurrentInstructor(data?.instructor || null);
      setCurrentAgency(data?.agency || null);
    } catch (err) {
      // 401s are handled by the axios interceptor (redirect to /login).
      // Anything else: leave state empty and let pages render their empty
      // states. Don't blow up the whole shell.
      // eslint-disable-next-line no-console
      console.error('Failed to load /me', err);
      setCurrentStudent(null);
      setCurrentInstructor(null);
      setCurrentAgency(null);
    } finally {
      setHydrating(false);
    }
  }, [user, profile]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <AppContext.Provider
      value={{
        role,
        currentStudent,
        currentInstructor,
        currentAgency,
        setCurrentStudent,
        setCurrentInstructor,
        setCurrentAgency,
        refreshData: hydrate,
        hydrating,
        signOut,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// Top-level redirect: send authenticated users to their dashboard,
// unauthenticated users to /login.
function RootRedirect() {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return <RootLoadingState />;
  if (!user || !profile) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <Navigate to={roleHome(profile)} replace />;
}

/** The retired course builder now resolves to the course room. */
function RedirectToCourseRoom() {
  const location = useLocation();
  return <Navigate to={location.pathname.replace(/\/builder\/?$/, '')} replace />;
}

function RootLoadingState() {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setStalled(true), 6000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="max-w-md text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#062B1A] flex items-center justify-center ring-1 ring-[#D4AF37]/30">
          <img src="/assets/mcc-logo-outlined.png" alt="MCC" className="w-9 h-9 object-contain animate-pulse" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <h1
          className="text-2xl font-semibold text-[#062B1A] tracking-tight"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Opening MCC Portal
        </h1>
        <p className="mt-3 text-sm text-[#062B1A]/65 leading-relaxed">
          We're checking your sign-in session and loading the right workspace.
        </p>
        {stalled && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-[#062B1A]/60">
              This is taking longer than expected. You can continue to the sign-in page now.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg bg-[#062B1A] px-4 py-2 text-sm font-medium text-white hover:bg-[#041B10] transition-colors"
            >
              Go to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function ShellRoutes() {
  return (
    <AppShell>
      <Routes>
        {['admin', 'student', 'instructor', 'agency'].map(role => <Route key={`inbox-${role}`} path={`/${role}/inbox`} element={<ProtectedRoute allow={[role]}><Inbox /></ProtectedRoute>} />)}
        {/* Student Routes */}
        <Route
          path="/student/dashboard"
          element={
            <ProtectedRoute allow={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        {/* /student/courses is the student's own enrolled courses; the
            browse-only catalog lives at /student/catalog. Keeping the course
            page nested under /student/courses/:id means the "My Courses" nav
            item stays highlighted while inside a course. */}
        <Route
          path="/student/courses"
          element={
            <ProtectedRoute allow={['student']}>
              <StudentMyCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/catalog"
          element={
            <ProtectedRoute allow={['student']}>
              <CourseCatalog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/courses/:courseId"
          element={
            <ProtectedRoute allow={['student']}>
              <CourseRoom />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/exams"
          element={<Navigate to="/student/courses" replace />}
        />
        <Route
          path="/student/exams/:examId/take"
          element={
            <ProtectedRoute allow={['student']}>
              <TakeExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/exams/:examId/review"
          element={
            <ProtectedRoute allow={['student']}>
              <ExamReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/assignments"
          element={<Navigate to="/student/courses" replace />}
        />
        <Route
          path="/student/live-sessions"
          element={<Navigate to="/student/courses" replace />}
        />
        <Route
          path="/student/live-sessions/:sessionId"
          element={<ProtectedRoute allow={['student']}><LiveMeetingRoom /></ProtectedRoute>}
        />
        <Route
          path="/student/progress"
          element={
            <ProtectedRoute allow={['student']}>
              <MyProgress />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/academic-record"
          element={
            <ProtectedRoute allow={['student']}>
              <AcademicRecord />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/finances"
          element={
            <ProtectedRoute allow={['student']}>
              <Finances />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/services"
          element={<ProtectedRoute allow={['student']}><BrowseServices /></ProtectedRoute>}
        />
        <Route
          path="/student/services/:productId"
          element={<ProtectedRoute allow={['student']}><ServiceProductDetail /></ProtectedRoute>}
        />
        <Route
          path="/student/services/:productId/checkout"
          element={<ProtectedRoute allow={['student']}><ServiceCheckout /></ProtectedRoute>}
        />
        <Route
          path="/student/orders"
          element={<ProtectedRoute allow={['student']}><MyServiceOrders /></ProtectedRoute>}
        />
        <Route
          path="/student/orders/:orderId"
          element={<ProtectedRoute allow={['student']}><ServiceOrderDetail /></ProtectedRoute>}
        />
        <Route
          path="/student/orders/:orderId/payment-result"
          element={<ProtectedRoute allow={['student']}><ServicePaymentResult /></ProtectedRoute>}
        />
        <Route
          path="/student/orders/:orderId/receipt"
          element={<ProtectedRoute allow={['student']}><ServiceReceiptInvoice /></ProtectedRoute>}
        />
        <Route
          path="/student/orders/:orderId/tracking"
          element={<ProtectedRoute allow={['student']}><ServiceTracking /></ProtectedRoute>}
        />
        <Route
          path="/student/orders/:orderId/shipping-upgrade"
          element={<ProtectedRoute allow={['student']}><ServiceShippingUpgrade /></ProtectedRoute>}
        />
        <Route
          path="/student/profile"
          element={
            <ProtectedRoute allow={['student']}>
              <StudentProfile />
            </ProtectedRoute>
          }
        />

        {/* Agency Routes */}
        <Route
          path="/agency/dashboard"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/referrals"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyReferrals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/event-links"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyEventLinks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/students"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyStudents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/commissions"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyCommissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/documents"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyDocuments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/profile"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/support"
          element={
            <ProtectedRoute allow={['agency']}>
              <AgencySupport />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/courses"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManageCourses />
            </ProtectedRoute>
          }
        />
        {/* An admin opens the same course room the class does — same layout,
            same classwork stream — because a course should look like one course
            to everybody who works on it. The registrar-only surfaces that the
            room has no place for (course metadata, the syllabus, registration
            and withdrawal) stay on the builder, one click away behind the
            room's "Course settings" button. /builder is kept as an alias so
            existing links and bookmarks still land somewhere useful. */}
        <Route
          path="/admin/courses/:courseId"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <CourseRoom />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/courses/:courseId/settings"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <CourseBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/courses/:courseId/builder"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <CourseBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/programs"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManagePrograms />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/programs/:programId"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <AdminProgramDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/exams"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManageExams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/assignments"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManageAssignments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/live-sessions"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManageLiveSessions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <ManageAnnouncements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <ManageStudents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students/:studentId"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminStudentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/applications"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminApplications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/applications/:id"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminApplicationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/chat"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminChat />
            </ProtectedRoute>
          }
        />
        {/* Website Engagement — the four inboxes the public site feeds:
            live chats, contact-form messages, newsletter opt-ins and event
            registrations.

            Website Messages and Event Signups keep their original addresses
            below. Every link already sent, every notification sitting in
            somebody's inbox and every bookmark still lands on the right page;
            the two /website-engagement/ aliases redirect onto them so a link
            written the new way works too. */}
        <Route
          path="/admin/website-engagement"
          element={<Navigate to="/admin/website-engagement/overview" replace />}
        />
        <Route
          path="/admin/website-engagement/messages"
          element={<Navigate to="/admin/website-messages" replace />}
        />
        <Route
          path="/admin/website-engagement/signups"
          element={<Navigate to="/admin/website-signups" replace />}
        />
        <Route
          path="/admin/website-engagement/overview"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <WebsiteEngagementOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/website-engagement/contact-inquiries"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminContactInquiries />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/website-engagement/contact-inquiries/:id"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminContactInquiryDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/website-engagement/newsletter"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminNewsletterSubscribers />
            </ProtectedRoute>
          }
        />
        {/* Event leads from the marketing site — their own record type, not
            admissions applications. */}
        <Route
          path="/admin/website-signups"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminWebsiteSignups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/website-signups/:id"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminWebsiteSignupDetail />
            </ProtectedRoute>
          }
        />
        {/* Language tests — English proficiency and French placement, in one
            section. Deleting a result needs full admin; the page disables the
            button rather than hiding it, and the server refuses either way. */}
        <Route
          path="/admin/language-tests"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminLanguageTests />
            </ProtectedRoute>
          }
        />
        {/* MCC Event Studio — events, their attendees and the legal text every
            registration is evidence against. */}
        <Route
          path="/admin/events"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:id"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminEventEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:id/registrations"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminEventRegistrations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/legal-policies"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminLegalPolicies />
            </ProtectedRoute>
          }
        />
        {/* Public visitor conversations. Team Chat above is unchanged. */}
        <Route
          path="/admin/website-messages"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminWebsiteMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agencies"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminAgencies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agencies/:id"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminAgencyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/instructors/new"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <CreateInstructor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/grades"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <AdminGrades />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketing"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminMarketing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/automations"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminAutomations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminReports />
            </ProtectedRoute>
          }
        />
        {/* Accounting. The bare path keeps working for every existing link and
            bookmark, landing on the section's default view. */}
        <Route path="/admin/accounting" element={<Navigate to="/admin/accounting/sales/invoices" replace />} />
        <Route path="/admin/accounting/sales" element={<Navigate to="/admin/accounting/sales/invoices" replace />} />
        <Route path="/admin/accounting/payments" element={<Navigate to="/admin/accounting/payments/bills" replace />} />
        <Route
          path="/admin/accounting/transactions"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingTransactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounting/sales/invoices"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingInvoices />
            </ProtectedRoute>
          }
        />
        {/* One invoice, with everything that has happened to it. The ref is
            `inv:<id>` or `doc:<id>`, url-encoded, so the page is linkable. */}
        <Route
          path="/admin/accounting/sales/invoices/:ref"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingInvoiceDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounting/sales/customers"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingCustomers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounting/sales/items"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingItems context="sell" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounting/payments/bills"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingBills />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounting/payments/items"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AccountingItems context="buy" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.STUDENT}>
              <AdminEmailTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/document-services/orders"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceOrders /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/orders/:orderId"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceOrderDetail /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/products"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceProducts /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/products/new"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceProductEditor /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/products/:productId"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceProductEditor /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/payments"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServicePayments /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/fulfillment"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceFulfillment /></ProtectedRoute>}
        />
        <Route
          path="/admin/document-services/reconciliation"
          element={<ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}><AdminServiceReconciliation /></ProtectedRoute>}
        />
        <Route
          path="/admin/commissions"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminCommissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/submissions"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <AdminSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounts"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ADMIN}>
              <AdminAccounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-intakes"
          element={
            <ProtectedRoute allow={['admin']} adminScope={ADMIN_SCOPES.ACADEMIC}>
              <AdminCourseIntakes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute allow={['admin']}>
              <AdminProfile />
            </ProtectedRoute>
          }
        />

        {/* Instructor Routes */}
        <Route
          path="/instructor/dashboard"
          element={
            <ProtectedRoute allow={['instructor']}>
              <InstructorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instructor/courses"
          element={
            <ProtectedRoute allow={['instructor']}>
              <InstructorCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instructor/courses/:courseId"
          element={
            <ProtectedRoute allow={['instructor']}>
              <CourseRoom />
            </ProtectedRoute>
          }
        />
        {/* Course authoring moved into the room itself, so the old builder
            link lands on the same page the class sees rather than 404ing. */}
        <Route
          path="/instructor/courses/:courseId/builder"
          element={<RedirectToCourseRoom />}
        />
        <Route
          path="/instructor/assignments"
          element={<Navigate to="/instructor/courses" replace />}
        />
        <Route
          path="/instructor/exams"
          element={<ProtectedRoute allow={['instructor']}><ManageExams instructorMode /></ProtectedRoute>}
        />
        <Route
          path="/instructor/live-sessions"
          element={<Navigate to="/instructor/courses" replace />}
        />
        <Route
          path="/instructor/live-sessions/:sessionId"
          element={<ProtectedRoute allow={['instructor']}><LiveMeetingRoom /></ProtectedRoute>}
        />
        <Route
          path="/instructor/submissions"
          element={<Navigate to="/instructor/courses" replace />}
        />
        <Route
          path="/instructor/announcements"
          element={
            <ProtectedRoute allow={['instructor']}>
              <InstructorAnnouncements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instructor/profile"
          element={
            <ProtectedRoute allow={['instructor']}>
              <InstructorProfile />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppContextProvider>
          <InboxProvider>
          <Routes>
            {/* Public auth routes (no shell) */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<Navigate to="/login" replace />} />
            <Route path="/auth/callback" element={<Navigate to="/login" replace />} />
            <Route path="/language-test/:token" element={<LanguageTest />} />
            {/* The address invitations used before the French test existed. Those
                links are in people's inboxes, so the route stays. */}
            <Route path="/english-assessment/:token" element={<LanguageTest />} />

            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* Everything else goes through the protected app shell */}
            <Route
              path="/*"
              element={
                <TopLevelErrorBoundary>
                  <ShellRoutes />
                </TopLevelErrorBoundary>
              }
            />
          </Routes>
          </InboxProvider>
        </AppContextProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
